import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { TaxRateKind } from '@/types/api';
import { canManageCountryTaxRates } from '@/utils/roleUtils';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import { FormField } from '@/components/molecules/FormField';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import { EUROPEAN_COUNTRY_CODES, countryName } from '@/utils/countries';
import Loader from '@/components/atoms/Loader/Loader';
import './CountryTaxRates.scss';

const CountryTaxRates: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  // Food rates (the historic `country_tax_rates` setting) and drink rates.
  const [taxRates, setTaxRates] = useState<Record<string, number>>({});
  const [drinkTaxRates, setDrinkTaxRates] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCountry, setEditingCountry] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newCountryCode, setNewCountryCode] = useState('');
  const [newFoodRate, setNewFoodRate] = useState('');
  const [newDrinkRate, setNewDrinkRate] = useState('');
  const [bulkJson, setBulkJson] = useState('');
  const [bulkKind, setBulkKind] = useState<TaxRateKind>('drink');

  // Check permissions
  if (!canManageCountryTaxRates(user)) {
    return (
      <div className="country-tax-rates-page">
        <div className="container">
          <Card variant="elevated" padding="lg">
            <p>{t('errors.unauthorized')}</p>
          </Card>
        </div>
      </div>
    );
  }

  const fetchTaxRates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllCountryTaxRates();
      setTaxRates(response.country_tax_rates || {});
      setDrinkTaxRates(response.country_drink_tax_rates || {});
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTaxRates();
  }, [fetchTaxRates]);

  const handleEdit = (countryCode: string) => {
    setEditingCountry(countryCode);
    setNewCountryCode(countryCode);
    setNewFoodRate(taxRates[countryCode]?.toString() || '');
    setNewDrinkRate(drinkTaxRates[countryCode]?.toString() || '');
  };

  const resetForm = () => {
    setNewCountryCode('');
    setNewFoodRate('');
    setNewDrinkRate('');
  };

  const handleCancelEdit = () => {
    setEditingCountry(null);
    resetForm();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const countryCode = newCountryCode.toUpperCase().trim();
    const drinkRate = parseFloat(newDrinkRate);
    const foodRate = parseFloat(newFoodRate);

    if (!countryCode || countryCode.length !== 2) {
      setError(t('admin.countryTaxRates.invalidCountryCode'));
      return;
    }

    if ([drinkRate, foodRate].some((rate) => isNaN(rate) || rate < 0 || rate > 100)) {
      setError(t('admin.countryTaxRates.invalidTaxRate'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      await apiService.updateCountryTaxRate(countryCode, drinkRate, 'drink');
      await apiService.updateCountryTaxRate(countryCode, foodRate, 'food');
      await fetchTaxRates();
      
      if (editingCountry) {
        setEditingCountry(null);
      } else {
        setShowAddForm(false);
      }
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError(null);

      let parsedRates: Record<string, number>;
      try {
        parsedRates = JSON.parse(bulkJson);
      } catch (parseError) {
        setError(t('admin.countryTaxRates.invalidJson'));
        return;
      }

      // Validate all rates
      for (const [code, rate] of Object.entries(parsedRates)) {
        if (code.length !== 2 || typeof rate !== 'number' || rate < 0 || rate > 100) {
          setError(t('admin.countryTaxRates.invalidBulkData', { code }));
          return;
        }
      }

      await apiService.bulkUpdateCountryTaxRates(parsedRates, bulkKind);
      await fetchTaxRates();
      setShowBulkForm(false);
      setBulkJson('');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (countryCode: string) => {
    if (!window.confirm(t('admin.countryTaxRates.deleteConfirm', { code: countryCode }))) {
      return;
    }

    try {
      setError(null);
      // Set both tax rates to 0 to effectively delete
      await apiService.updateCountryTaxRate(countryCode, 0, 'drink');
      await apiService.updateCountryTaxRate(countryCode, 0, 'food');
      await fetchTaxRates();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    }
  };

  const allCodes = Array.from(new Set([...Object.keys(taxRates), ...Object.keys(drinkTaxRates)]));
  const filteredCodes = allCodes.filter((code) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const name = countryName(code, i18n.language).toLowerCase();
      return code.toLowerCase().includes(query) || name.includes(query);
    }
    return (taxRates[code] || 0) > 0 || (drinkTaxRates[code] || 0) > 0; // Only show non-zero rates by default
  }).sort((a, b) => a.localeCompare(b));

  const formatRate = (rate: number | undefined) => (rate === undefined ? '—' : `${rate.toFixed(2)}%`);
  const bulkTable = (kind: TaxRateKind) => JSON.stringify(kind === 'drink' ? drinkTaxRates : taxRates, null, 2);

  const getCountryName = (code: string) => countryName(code, i18n.language);

  const countryOptions = EUROPEAN_COUNTRY_CODES
    .map((code) => ({ value: code as string, label: `${countryName(code, i18n.language)} (${code})` }))
    .sort((a, b) => a.label.localeCompare(b.label, i18n.language));

  if (loading) {
    return (
      <div className="country-tax-rates-page">
        <div className="container">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="country-tax-rates-page">
      <div className="container">
        <div className="country-tax-rates-page__header">
          <h1 className="country-tax-rates-page__title">
            {t('admin.countryTaxRates.title')}
          </h1>
          <p className="country-tax-rates-page__description">
            {t('admin.countryTaxRates.description')}
          </p>
        </div>

        {error && (
          <Card variant="elevated" padding="md" className="country-tax-rates-page__error">
            <p>{error}</p>
          </Card>
        )}

        {/* Search and Actions */}
        <Card variant="elevated" padding="lg" className="country-tax-rates-page__filters">
          <div className="country-tax-rates-page__filters-content">
            <FormField
              type="text"
              name="search"
              label={t('admin.countryTaxRates.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('admin.countryTaxRates.searchPlaceholder')}
            />
            <div className="country-tax-rates-page__actions">
              <Button
                variant="primary"
                onClick={() => {
                  setShowAddForm(true);
                  setEditingCountry(null);
                  resetForm();
                }}
              >
                {t('admin.countryTaxRates.addCountry')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkForm(true);
                  setBulkJson(bulkTable(bulkKind));
                }}
              >
                {t('admin.countryTaxRates.bulkUpdate')}
              </Button>
            </div>
          </div>
        </Card>

        {/* Add/Edit Form */}
        {(showAddForm || editingCountry) && (
          <Card variant="elevated" padding="lg" className="country-tax-rates-page__form-card">
            <h3>{editingCountry ? t('admin.countryTaxRates.editCountry') : t('admin.countryTaxRates.addCountry')}</h3>
            <form onSubmit={handleSave}>
              <div className="country-tax-rates-page__form-fields">
                <SearchableSelect<string>
                  name="country_code"
                  label={t('admin.countryTaxRates.countryCode')}
                  options={
                    // Keep a country that is already configured selectable even if it's outside Europe.
                    newCountryCode && !countryOptions.some((o) => o.value === newCountryCode)
                      ? [{ value: newCountryCode, label: `${getCountryName(newCountryCode)} (${newCountryCode})` }, ...countryOptions]
                      : countryOptions
                  }
                  value={newCountryCode || null}
                  onChange={(value) => setNewCountryCode(value ?? '')}
                  required
                  disabled={!!editingCountry}
                  helperText={t('admin.countryTaxRates.countryCodeHelp')}
                />
                <FormField
                  type="number"
                  name="drink_tax_rate"
                  label={t('admin.countryTaxRates.drinkRate')}
                  value={newDrinkRate}
                  onChange={(e) => setNewDrinkRate(e.target.value)}
                  placeholder="21"
                  min="0"
                  max="100"
                  step="0.1"
                  required
                  helperText={t('admin.countryTaxRates.drinkRateHelp')}
                />
                <FormField
                  type="number"
                  name="food_tax_rate"
                  label={t('admin.countryTaxRates.foodRate')}
                  value={newFoodRate}
                  onChange={(e) => setNewFoodRate(e.target.value)}
                  placeholder="10"
                  min="0"
                  max="100"
                  step="0.1"
                  required
                  helperText={t('admin.countryTaxRates.foodRateHelp')}
                />
              </div>
              <div className="country-tax-rates-page__form-actions">
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {t('common.save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    handleCancelEdit();
                    setShowAddForm(false);
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Bulk Update Form */}
        {showBulkForm && (
          <Card variant="elevated" padding="lg" className="country-tax-rates-page__form-card">
            <h3>{t('admin.countryTaxRates.bulkUpdate')}</h3>
            <form onSubmit={handleBulkUpdate}>
              <FormField
                type="select"
                name="bulk_kind"
                label={t('admin.countryTaxRates.bulkKind')}
                value={bulkKind}
                onChange={(e) => {
                  const kind = e.target.value as TaxRateKind;
                  setBulkKind(kind);
                  setBulkJson(bulkTable(kind));
                }}
              >
                <option value="drink">{t('admin.countryTaxRates.drinkRate')}</option>
                <option value="food">{t('admin.countryTaxRates.foodRate')}</option>
              </FormField>
              <FormField
                type="textarea"
                name="bulk_json"
                label={t('admin.countryTaxRates.bulkJson')}
                value={bulkJson}
                onChange={(e) => setBulkJson(e.target.value)}
                required
                helperText={t('admin.countryTaxRates.bulkJsonHelp')}
                rows={10}
              />
              <div className="country-tax-rates-page__form-actions">
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {t('common.save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowBulkForm(false);
                    setBulkJson('');
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Tax Rates Table */}
        <Card variant="elevated" padding="lg" className="country-tax-rates-page__table-card">
          <div className="country-tax-rates-page__table">
            <div className="country-tax-rates-page__table-header">
              <div className="country-tax-rates-page__table-cell country-tax-rates-page__table-cell--header">
                {t('admin.countryTaxRates.countryCode')}
              </div>
              <div className="country-tax-rates-page__table-cell country-tax-rates-page__table-cell--header">
                {t('admin.countryTaxRates.countryName')}
              </div>
              <div className="country-tax-rates-page__table-cell country-tax-rates-page__table-cell--header">
                {t('admin.countryTaxRates.drinkRate')}
              </div>
              <div className="country-tax-rates-page__table-cell country-tax-rates-page__table-cell--header">
                {t('admin.countryTaxRates.foodRate')}
              </div>
              <div className="country-tax-rates-page__table-cell country-tax-rates-page__table-cell--header">
                {t('common.actions')}
              </div>
            </div>
            {filteredCodes.map((code) => (
              <div key={code} className="country-tax-rates-page__table-row">
                <div className="country-tax-rates-page__table-cell">
                  <strong>{code}</strong>
                </div>
                <div className="country-tax-rates-page__table-cell">
                  {getCountryName(code)}
                </div>
                <div className="country-tax-rates-page__table-cell">
                  {formatRate(drinkTaxRates[code])}
                </div>
                <div className="country-tax-rates-page__table-cell">
                  {formatRate(taxRates[code])}
                </div>
                <div className="country-tax-rates-page__table-cell">
                  <div className="country-tax-rates-page__table-actions">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(code)}
                    >
                      {t('common.edit')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(code)}
                    >
                      {t('common.delete')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {filteredCodes.length === 0 && (
            <p className="country-tax-rates-page__empty">{t('admin.countryTaxRates.noRates')}</p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default CountryTaxRates;

